"use client"

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGetUnifiedCampaignByCodeQuery } from "@/services/campaign";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2, Calendar, Store, Settings, Package, List, Edit, ChevronDown } from "lucide-react";
import { Pro014ItemsModal } from "./pro014-items-modal";
import { ItemGradesSection } from "./item-grades-section";
import { formatPromotionText } from "../utils/format-promotion-text";
import { CampaignType } from "@/@types/campaign";
import type { IUnifiedCampaignCodeRef, IUnifiedCampaignList } from "@/@types/campaign";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { PRO014_STORES_TEXTS } from "./edit-campaign/constants";
import {
  GRADE_RESTRICTION_LABEL,
  RESTRICTION_OFF_SHORT,
  RESTRICTION_ON_SHORT,
} from "./grade/grade-messages";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";

interface UnifiedCampaignDetailsContentProps {
  campaignCode: string;
}

/**
 * Capabilities por tipo de campanha — Record é a fonte da verdade
 * para decidir o que cada tipo renderiza no modal "Ver Detalhes".
 */
type CampaignCapabilities = {
  showsTakePayRule: boolean;
  showsComboRule: boolean;
  showsDiscountValue: boolean;
  showsNumLists: boolean;
  showsPaymentMethod: boolean;
  showsItems: boolean;
  showsLists: boolean;
};

const CAPABILITIES_BY_TYPE: Record<CampaignType, CampaignCapabilities> = {
  [CampaignType.PRO012]: {
    showsTakePayRule: true,
    showsComboRule: false,
    showsDiscountValue: true,
    showsNumLists: false,
    showsPaymentMethod: true,
    showsItems: true,
    showsLists: false,
  },
  [CampaignType.PRO013]: {
    showsTakePayRule: true,
    showsComboRule: false,
    showsDiscountValue: true,
    showsNumLists: false,
    showsPaymentMethod: true,
    showsItems: false,
    showsLists: false,
  },
  [CampaignType.PRO014]: {
    showsTakePayRule: true,
    showsComboRule: false,
    showsDiscountValue: true,
    showsNumLists: false,
    showsPaymentMethod: true,
    showsItems: true,
    showsLists: false,
  },
  [CampaignType.PRO017]: {
    showsTakePayRule: false,
    showsComboRule: true,
    showsDiscountValue: false,
    showsNumLists: true,
    showsPaymentMethod: false,
    showsItems: false,
    showsLists: true,
  },
};

const NO_CAPABILITIES: CampaignCapabilities = {
  showsTakePayRule: false,
  showsComboRule: false,
  showsDiscountValue: false,
  showsNumLists: false,
  showsPaymentMethod: false,
  showsItems: false,
  showsLists: false,
};

const getCapabilities = (type: string): CampaignCapabilities =>
  CAPABILITIES_BY_TYPE[type as CampaignType] ?? NO_CAPABILITIES;

const getStatusBadge = (status: string) => {
  const statusConfig = {
    ACTIVE: { label: "Ativa", variant: "default" as const },
    FUTURE: { label: "Futura", variant: "secondary" as const },
    EXPIRED: { label: "Expirada", variant: "outline" as const },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: "secondary" as const };
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getTypeBadge = (type: string) => {
  const typeConfig: Record<string, string> = {
    PRO012: "Combo Legado",
    PRO013: "Leve X Pague Y",
    PRO014: "Promoção Itens",
    PRO017: "Combo Moderno",
  };

  return <Badge variant="outline">{typeConfig[type] || type}</Badge>;
};

export function UnifiedCampaignDetailsContent({ campaignCode }: UnifiedCampaignDetailsContentProps) {
  const { data: campaign, isLoading, error } = useGetUnifiedCampaignByCodeQuery(campaignCode);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const capabilities = getCapabilities(campaign?.type ?? "");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <p className="text-destructive">Erro ao carregar campanha</p>
        <p className="text-sm text-muted-foreground">{extractRtkErrorMessage(error, 'Erro desconhecido')}</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Campanha não encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com Status e Tipo */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">Código: #{campaign.code}</p>
          {getTypeBadge(campaign.type)}
        </div>
        <div className="flex-shrink-0">
          {getStatusBadge(campaign.visualStatus)}
        </div>
      </div>

      {/* Informações Gerais */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Calendar className="w-4 h-4" />
          Informações Gerais
        </div>
        <div className="rounded-lg p-3 bg-secondary/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Data de Início</p>
              <p className="font-semibold text-sm">
                {campaign.startDate && format(new Date(campaign.startDate), "dd/MM/yyyy", { locale: ptBR })}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Data de Término</p>
              <p className="font-semibold text-sm">
                {campaign.endDate && format(new Date(campaign.endDate), "dd/MM/yyyy", { locale: ptBR })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lojas */}
      {(campaign.allStores || (campaign.stores && campaign.stores.length > 0)) && (
        <StoresSection
          stores={participatingStores(campaign.stores || [])}
          allStores={!!campaign.allStores}
        />
      )}

      {/* Configurações da Campanha */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Settings className="w-4 h-4" />
          Configurações da Campanha
        </div>
        <div className="rounded-lg p-3 bg-secondary/20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {capabilities.showsTakePayRule &&
              campaign.takeQuantity != null &&
              campaign.payQuantity != null && (
                <>
                  <div className="col-span-full md:col-span-2">
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Regra da Promoção</p>
                    <p className="font-semibold text-sm">
                      {formatPromotionText(campaign.takeQuantity, campaign.payQuantity, campaign.discountType ?? 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Leve</p>
                    <p className="font-semibold text-sm">{campaign.takeQuantity}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Pague/Desconto em</p>
                    <p className="font-semibold text-sm">{campaign.payQuantity}</p>
                  </div>
                </>
              )}

            {capabilities.showsComboRule && campaign.discountValue != null && (
              <div className="col-span-full">
                <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Regra do Combo</p>
                <p className="font-semibold text-sm">
                  Combo: {campaign.discountType === 1
                    ? `${campaign.discountValue}% de desconto`
                    : `R$ ${Number(campaign.discountValue).toFixed(2)} de desconto`}
                  {campaign.numLists ? ` • ${campaign.numLists} ${campaign.numLists === 1 ? "lista" : "listas"}` : ""}
                </p>
              </div>
            )}

            {capabilities.showsDiscountValue &&
              campaign.discountValue !== null &&
              campaign.discountValue !== undefined && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Valor do Desconto</p>
                  <p className="font-bold text-sm">
                    {campaign.discountType === 1
                      ? `${campaign.discountValue}%`
                      : `R$ ${Number(campaign.discountValue).toFixed(2)}`}
                  </p>
                </div>
              )}

            {capabilities.showsNumLists && campaign.numLists && (
              <div>
                <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Número de Listas</p>
                <p className="font-semibold text-sm">{campaign.numLists}</p>
              </div>
            )}

            {capabilities.showsPaymentMethod &&
              campaign.paymentMethod !== null &&
              campaign.paymentMethod !== undefined && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Forma de Pagamento</p>
                  <p className="font-semibold text-sm">Todas as Formas</p>
                </div>
              )}

            {campaign.type === CampaignType.PRO014 && typeof campaign.gradeRestricted === "boolean" && (
              <div>
                <p className="text-xs text-muted-foreground uppercase font-medium mb-1">
                  {GRADE_RESTRICTION_LABEL}
                </p>
                <p className="font-semibold text-sm" data-testid="details-grade-restriction">
                  {campaign.gradeRestricted ? RESTRICTION_ON_SHORT : RESTRICTION_OFF_SHORT}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Departamentos (PRO013) */}
      {campaign.departments && campaign.departments.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <List className="w-4 h-4" />
            Departamentos ({campaign.departments.length})
          </div>
          <div className="rounded-lg p-3 bg-secondary/20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {campaign.departments.map((dept: IUnifiedCampaignCodeRef, index: number) => (
                <Badge key={index} variant="outline" className="justify-center">
                  {dept.code}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grupos (PRO013) */}
      {campaign.groups && campaign.groups.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Package className="w-4 h-4" />
            Grupos de Produtos ({campaign.groups.length})
          </div>
          <div className="rounded-lg p-3 bg-secondary/20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {campaign.groups.map((group: IUnifiedCampaignCodeRef, index: number) => (
                <Badge key={index} variant="outline" className="justify-center">
                  {group.code}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Itens (PRO012, PRO014) */}
      {capabilities.showsItems && campaign.items && campaign.items.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Package className="w-4 h-4" />
              Itens da Campanha ({campaign.items.length})
            </div>
            {campaign.type === CampaignType.PRO014 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsItemsModalOpen(true)}
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Gerenciar Itens
              </Button>
            )}
          </div>
          <div className="rounded-lg p-3 bg-secondary/20">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {campaign.items.map((item: IUnifiedCampaignCodeRef, index: number) => (
                <Badge key={index} variant="outline" className="justify-center py-1.5">
                  {item.code}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {campaign.type === CampaignType.PRO014 && campaign.gradeRestricted && (
        <ItemGradesSection campaignCode={Number(campaign.code)} />
      )}

      {/* Listas (PRO017) */}
      {capabilities.showsLists && campaign.lists && campaign.lists.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <List className="w-4 h-4" />
            Listas de Produtos ({campaign.lists.length})
          </div>
          <div className="rounded-lg p-3 bg-secondary/20 space-y-3">
            {campaign.lists.map((list: IUnifiedCampaignList, index: number) => (
              <div key={index} className="bg-background rounded-lg p-3 border">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-sm">
                    Lista {list.listNumber}
                  </div>
                  <Badge variant="secondary">
                    {list.items?.length || 0} itens
                  </Badge>
                </div>
                {list.items && list.items.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {list.items.map((item: IUnifiedCampaignCodeRef, itemIndex: number) => (
                      <Badge key={itemIndex} variant="outline" className="justify-center py-1.5">
                        {item.code}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de Gerenciamento de Itens PRO014 */}
      {campaign.type === CampaignType.PRO014 && (
        <Pro014ItemsModal
          campaignCode={Number(campaign.code)}
          isOpen={isItemsModalOpen}
          onClose={() => setIsItemsModalOpen(false)}
          gradeRestricted={campaign.gradeRestricted === true}
        />
      )}
    </div>
  );
}

interface StoresSectionProps {
  stores: Array<{
    code: number | string;
    name?: string;
    startDate?: string;
    endDate?: string;
    visualStatus?: string;
    participating?: boolean;
  }>;
  allStores: boolean;
}

const participatingStores = <T extends { participating?: boolean }>(stores: T[]): T[] =>
  stores.filter((store: T) => store.participating !== false);

/**
 * Seção de lojas: header com resumo + Collapsible com ScrollArea para a lista.
 * Default fechado quando `allStores=true` (caso típico com 200+ lojas).
 * Default aberto quando há poucas lojas específicas selecionadas.
 */
function StoresSection({ stores, allStores }: StoresSectionProps) {
  const [open, setOpen] = useState(!allStores);
  const total = stores.length;
  const summary = allStores
    ? `Todas as Lojas${total > 0 ? ` (${total})` : ""}`
    : `${total} ${total === 1 ? "loja" : "lojas"}`;

  return (
    <div className="space-y-2">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 rounded-md p-2 text-sm font-medium text-muted-foreground hover:bg-secondary/40 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Store className="w-4 h-4" />
              {summary}
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {total === 0 ? (
            <div className="rounded-lg p-3 bg-secondary/20 text-xs text-muted-foreground">
              Lista de lojas não disponível.
            </div>
          ) : (
            <ScrollArea className="h-64 rounded-lg border bg-secondary/20 p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {stores.map((store, index) => (
                  <div
                    key={`${store.code}-${index}`}
                    className="flex flex-col gap-1 bg-background rounded-md p-2 border"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {store.code}
                      </Badge>
                      {store.name && store.name !== `Loja ${store.code}` && (
                        <span className="text-xs truncate">{store.name}</span>
                      )}
                    </div>
                    {store.startDate && store.endDate && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {PRO014_STORES_TEXTS.WINDOW_LABEL(
                          formatDateToDMY(store.startDate),
                          formatDateToDMY(store.endDate),
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
